'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import { ApiResponse } from '@/lib/types/anthropic';

interface VisaApplication {
  id: string;
  applicationType: string;
  destination: string;
  currentStatus: string;
  estimatedCompletionDate: string;
  travelDate: string;
  urgencyLevel: string;
}

interface UrgencyAnalysisData {
  urgencyScore: number;
  daysUntilTravel: number;
  processingTimeRemaining: number;
  riskLevel: 'low' | 'medium' | 'high' | 'critical';
  recommendations: {
    type: 'expedited-service' | 'document-prep' | 'travel-insurance' | 'alternative-plan';
    title: string;
    description: string;
    priority: 'high' | 'medium' | 'low';
    estimatedCost?: string;
    actionUrl?: string;
  }[];
  insights: string[];
}

interface UrgencyAnalysisProps {
  application: VisaApplication;
}

export default function UrgencyAnalysis({ application }: UrgencyAnalysisProps) {
  const [analysis, setAnalysis] = useState<UrgencyAnalysisData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    analyzeUrgency();
  }, [application.id]);

  const analyzeUrgency = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch('/api/anthropic/visa-urgency-analysis', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          application: {
            applicationType: application.applicationType,
            destination: application.destination,
            currentStatus: application.currentStatus,
            estimatedCompletionDate: application.estimatedCompletionDate,
            travelDate: application.travelDate,
            urgencyLevel: application.urgencyLevel
          }
        }),
      });

      const data: ApiResponse<UrgencyAnalysisData> = await response.json();

      if (data.success) {
        setAnalysis(data.data);
      } else {
        setError(data.error.message);
        if (!data.error.isInternal) {
          console.error('Urgency analysis error:', data.error.message);
        }
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to analyze urgency';
      setError(errorMessage);
      console.error('Urgency analysis error:', err);
    } finally {
      setLoading(false);
    }
  };

  const riskConfig = {
    low: { color: 'bg-trust-green/10 text-trust-green border-trust-green/30', label: 'Low Risk', icon: 'CheckCircleIcon' },
    medium: { color: 'bg-warning/10 text-warning border-warning/30', label: 'Medium Risk', icon: 'ExclamationCircleIcon' },
    high: { color: 'bg-coral/10 text-coral border-coral/30', label: 'High Risk', icon: 'ExclamationTriangleIcon' },
    critical: { color: 'bg-error/10 text-error border-error/30', label: 'Critical Risk', icon: 'ShieldExclamationIcon' }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-brand p-6 border-2 border-border">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
            <Icon name="SparklesIcon" size={20} className="text-primary" />
          </div>
          <h3 className="font-heading font-bold text-xl text-foreground">
            AI Urgency Analysis
          </h3>
        </div>
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-muted rounded w-3/4"></div>
          <div className="h-4 bg-muted rounded w-1/2"></div>
          <div className="h-20 bg-muted rounded"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-xl shadow-brand p-6 border-2 border-error/20">
        <div className="flex items-center gap-3 mb-4">
          <Icon name="ExclamationCircleIcon" size={24} className="text-error" />
          <h3 className="font-heading font-bold text-xl text-foreground">
            AI Urgency Analysis
          </h3>
        </div>
        <p className="text-sm text-error mb-4">{error}</p>
        <button
          onClick={analyzeUrgency}
          className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors text-sm font-medium"
        >
          Retry Analysis
        </button>
      </div>
    );
  }

  if (!analysis) return null;

  const risk = riskConfig[analysis.riskLevel];

  return (
    <div className="bg-white rounded-xl shadow-brand p-6 border-2 border-border">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
          <Icon name="SparklesIcon" size={20} className="text-white" />
        </div>
        <div className="flex-1">
          <h3 className="font-heading font-bold text-xl text-foreground">
            AI Urgency Analysis
          </h3>
          <p className="text-xs text-muted-foreground">Powered by Claude AI</p>
        </div>
        <div className={`px-4 py-2 rounded-lg border-2 font-bold text-sm ${risk.color}`}>
          <div className="flex items-center gap-2">
            <Icon name={risk.icon as any} size={16} />
            {risk.label}
          </div>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-surface rounded-lg p-4 border border-border">
          <p className="text-xs text-muted-foreground mb-1">Urgency Score</p>
          <p className="text-2xl font-black text-foreground">{analysis.urgencyScore}/100</p>
        </div>
        <div className="bg-surface rounded-lg p-4 border border-border">
          <p className="text-xs text-muted-foreground mb-1">Days Until Travel</p>
          <p className="text-2xl font-black text-foreground">{analysis.daysUntilTravel}</p>
        </div>
        <div className="bg-surface rounded-lg p-4 border border-border">
          <p className="text-xs text-muted-foreground mb-1">Processing Days Left</p>
          <p className="text-2xl font-black text-foreground">{analysis.processingTimeRemaining}</p>
        </div>
      </div>

      {/* Insights */}
      {analysis.insights.length > 0 && (
        <div className="mb-6">
          <h4 className="font-heading font-semibold text-sm text-foreground mb-3">Key Insights</h4>
          <div className="space-y-2">
            {analysis.insights.map((insight, index) => (
              <div key={index} className="flex items-start gap-2 text-sm text-muted-foreground">
                <Icon name="LightBulbIcon" size={16} className="text-primary mt-0.5 flex-shrink-0" />
                <span>{insight}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recommendations */}
      {analysis.recommendations.length > 0 && (
        <div>
          <h4 className="font-heading font-semibold text-sm text-foreground mb-3">Recommended Actions</h4>
          <div className="space-y-3">
            {analysis.recommendations.map((rec, index) => (
              <div key={index} className="bg-surface rounded-lg p-4 border border-border hover:border-primary/30 transition-colors">
                <div className="flex items-start justify-between mb-2">
                  <h5 className="font-heading font-semibold text-foreground">{rec.title}</h5>
                  {rec.priority === 'high' && (
                    <span className="px-2 py-1 bg-coral/10 text-coral text-xs font-bold rounded">
                      HIGH PRIORITY
                    </span>
                  )}
                </div>
                <p className="text-sm text-muted-foreground mb-3">{rec.description}</p>
                <div className="flex items-center justify-between">
                  {rec.estimatedCost && (
                    <span className="text-sm font-bold text-primary">{rec.estimatedCost}</span>
                  )}
                  {rec.actionUrl && (
                    <a
                      href={rec.actionUrl}
                      className="text-sm font-medium text-primary hover:text-primary/80 transition-colors"
                    >
                      View Details →
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}